import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { OrderStatus } from '@reims-local/types';
import { orderApi } from '../lib/api';

type OrderDetail = Awaited<ReturnType<typeof orderApi.get>>['data'];

export function OrderTrack() {
  const { id } = useParams<{ id: string }>();
  const [detail, setDetail] = useState<OrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    if (!id) return;
    try {
      const res = await orderApi.get(id);
      setDetail(res.data);
      setError(null);
    } catch (err) {
      const code = (err as Error).message;
      setError(code === 'not_authenticated' ? 'Connectez-vous pour voir cette commande.' : 'Commande introuvable.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    const intv = setInterval(() => void load(), 30_000);
    return () => clearInterval(intv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) return <main className="container"><p>Chargement…</p></main>;
  if (error) return <main className="container"><div className="alert alert-error">{error}</div></main>;
  if (!detail) return null;

  const { order, items, shop } = detail;
  const status = order.status as OrderStatus;

  return (
    <main className="container">
      <Link to="/mes-commandes">← Mes commandes</Link>
      <h2 style={{ marginTop: 12 }}>Commande {order.order_number}</h2>

      <div className="card">
        <StatusTracker current={status} />
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>{shop?.name}</h3>
        {shop?.address && <p className="muted" style={{ margin: 0 }}>📍 {shop.address}</p>}
        {shop?.phone && <p className="muted" style={{ margin: 0 }}>📞 {shop.phone}</p>}
        <p style={{ marginTop: 12 }}>
          <strong>Retrait prévu :</strong> {formatPickup(order.pickup_slot)}
        </p>
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Articles</h3>
        <table>
          <tbody>
            {items.map((it) => (
              <tr key={it.id}>
                <td>{it.product?.name ?? '(supprimé)'}</td>
                <td style={{ textAlign: 'right', width: 60 }}>× {it.quantity}</td>
                <td style={{ textAlign: 'right', width: 100 }}>{((it.unit_price_cents * it.quantity) / 100).toFixed(2)} €</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td><strong>Total à régler au retrait</strong></td>
              <td></td>
              <td style={{ textAlign: 'right' }}><strong>{(order.total_cents / 100).toFixed(2)} €</strong></td>
            </tr>
          </tfoot>
        </table>
      </div>

      {order.cancel_reason && (
        <div className="alert alert-error">
          Commande annulée : {order.cancel_reason}
        </div>
      )}
    </main>
  );
}

function StatusTracker({ current }: { current: OrderStatus }) {
  if (current === 'cancelled') {
    return <div className="badge red">Commande annulée</div>;
  }
  const steps: OrderStatus[] = ['confirmed', 'preparing', 'ready', 'picked_up'];
  const labels: Record<OrderStatus, string> = {
    pending: 'En attente', confirmed: 'Confirmée', preparing: 'En préparation',
    ready: 'Prête', picked_up: 'Récupérée', cancelled: 'Annulée',
  };
  const currentIdx = steps.indexOf(current);
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      {steps.map((s, i) => (
        <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span className={`badge ${i <= currentIdx ? 'green' : ''}`}>
            {i < currentIdx ? '✓ ' : i === currentIdx ? '● ' : ''}{labels[s]}
          </span>
          {i < steps.length - 1 && <span style={{ color: 'var(--muted)' }}>→</span>}
        </div>
      ))}
    </div>
  );
}

function formatPickup(iso: string): string {
  try {
    return new Intl.DateTimeFormat('fr-FR', {
      timeZone: 'Europe/Paris', weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit',
    }).format(new Date(iso));
  } catch { return iso; }
}
